import { showToast } from './utils.js';
import {
  SUPPORTED_COST_MODELS,
  guessModelProvider,
  normalizeModelRateEntry,
} from './cost-calculation-model-contract.js';

const COST_KEYS = Object.freeze({
  image: {
    fee: 'cost.image.totalFee',
    units: 'cost.image.totalUnits',
  },
  marketTools: {
    sellersprite: {
      cycle: 'cost.marketTools.sellersprite.billingCycle',
      fee: 'cost.marketTools.sellersprite.totalFee',
      calls: 'cost.marketTools.sellersprite.totalCalls',
      balance: 'cost.marketTools.sellersprite.openingBalance',
    },
    sorftime: {
      cycle: 'cost.marketTools.sorftime.billingCycle',
      fee: 'cost.marketTools.sorftime.totalFee',
      calls: 'cost.marketTools.sorftime.totalCalls',
      balance: 'cost.marketTools.sorftime.openingBalance',
    },
  },
  modelRates: 'cost.modelRates',
});

/**
 * 费用配置读写控制器。
 *
 * 运行时配置的所有旧字段形状都在此处归一化；页面协调器只消费稳定的
 * image / marketTools / modelRates 视图模型，并在保存成功后重载完整页面状态。
 */
export function createCostCalculationConfigurationController({
  getRuntimeSettings,
  setRuntimeSettings,
  reload,
  saveRuntimeSettings,
}) {
  function getSettingMap() {
    const map = new Map();
    for (const item of getRuntimeSettings?.() || []) {
      if (item && typeof item.key === 'string') map.set(item.key, item);
    }
    return map;
  }

  function getPersistedConfiguration() {
    const settingMap = getSettingMap();
    return {
      settingMap,
      image: getImageConfig(settingMap),
      marketTools: getMarketToolConfig(settingMap),
      modelRates: getModelRates(settingMap),
      modelCandidates: getModelCandidates(),
    };
  }

  function getDomConfiguration() {
    return {
      image: getImageConfigFromDom(),
      marketTools: getMarketToolConfigFromDom(),
      modelRates: getModelRatesFromDom(),
      modelCandidates: getModelCandidates(),
    };
  }

  function getImageConfig(settingMap) {
    return {
      totalFee: Number(settingMap.get(COST_KEYS.image.fee)?.value || 0),
      totalUnits: Number(settingMap.get(COST_KEYS.image.units)?.value || 0),
    };
  }

  function getImageConfigFromDom() {
    const totalFee = getFiniteNumber('cost-image-fee');
    const totalUnits = getFiniteNumber('cost-image-units');
    return { totalFee, totalUnits };
  }

  function getMarketToolConfig(settingMap) {
    return {
      sellersprite: readMarketToolSetting(settingMap, 'sellersprite'),
      sorftime: readMarketToolSetting(settingMap, 'sorftime'),
    };
  }

  function readMarketToolSetting(settingMap, provider) {
    const keys = COST_KEYS.marketTools[provider];
    return {
      billingCycle: String(settingMap.get(keys.cycle)?.value || 'month'),
      totalFee: Number(settingMap.get(keys.fee)?.value || 0),
      totalCalls: Number(settingMap.get(keys.calls)?.value || 0),
      openingBalance: Number(settingMap.get(keys.balance)?.value || 0),
    };
  }

  function getMarketToolConfigFromDom() {
    return {
      sellersprite: readMarketToolDom('sellersprite'),
      sorftime: readMarketToolDom('sorftime'),
    };
  }

  function readMarketToolDom(provider) {
    return {
      billingCycle: document.getElementById(`cost-${provider}-cycle`)?.value || 'month',
      totalFee: getFiniteNumber(`cost-${provider}-fee`),
      totalCalls: getFiniteNumber(`cost-${provider}-calls`),
      openingBalance: getFiniteNumber(`cost-${provider}-balance`),
    };
  }

  function getFiniteNumber(id) {
    const value = Number(document.getElementById(id)?.value || 0);
    return Number.isFinite(value) ? value : 0;
  }

  function getModelCandidates() {
    return [...SUPPORTED_COST_MODELS];
  }

  function getModelRates(settingMap) {
    const config = settingMap.get(COST_KEYS.modelRates)?.value;
    if (!config || typeof config !== 'object') return {};
    if (Array.isArray(config)) {
      return config.reduce((rates, entry) => {
        const name = String(entry?.name || '').trim();
        if (!name) return rates;
        rates[name] = {
          provider: String(entry.provider || '').trim() || undefined,
          ...normalizeModelRateEntry(entry),
        };
        return rates;
      }, {});
    }
    return Object.entries(config).reduce((rates, [name, entry]) => {
      const modelName = String(name || '').trim();
      if (!modelName) return rates;
      if (typeof entry === 'number') {
        rates[modelName] = { inputPerMillion: Number(entry), outputPerMillion: Number(entry) };
      } else if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        rates[modelName] = {
          provider: String(entry.provider || '').trim(),
          ...normalizeModelRateEntry(entry),
        };
      }
      return rates;
    }, {});
  }

  function getModelRatesFromDom() {
    const rates = {};
    document.querySelectorAll('tr[data-model-name]').forEach((row) => {
      const modelName = String(row.getAttribute('data-model-name') || '').trim();
      if (!modelName) return;
      const provider = String(row.getAttribute('data-model-provider') || '').trim() || 'minimax';
      const modelKey = String(row.getAttribute('data-model-key') || '');
      const inputElement = row.querySelector(`.cost-model-input[data-model-key="${cssEscape(modelKey)}"]`);
      const outputElement = row.querySelector(`.cost-model-output[data-model-key="${cssEscape(modelKey)}"]`);
      const inputPerMillion = Number(inputElement?.value || 0);
      const outputPerMillion = Number(outputElement?.value || 0);
      rates[modelName] = {
        provider,
        inputPerMillion: Number.isFinite(inputPerMillion) ? inputPerMillion : 0,
        outputPerMillion: Number.isFinite(outputPerMillion) ? outputPerMillion : 0,
      };
    });
    return rates;
  }

  async function save() {
    const button = document.getElementById('cost-calc-save-btn');
    if (button) button.disabled = true;
    try {
      const { image, marketTools, modelRates, modelCandidates } = getDomConfiguration();
      const modelRows = Object.entries(modelRates).reduce((rows, [modelName, config]) => {
        if (!modelName) return rows;
        rows[modelName] = {
          provider: config.provider || guessModelProvider(modelName, modelCandidates),
          inputPerMillion: config.inputPerMillion,
          outputPerMillion: config.outputPerMillion,
        };
        return rows;
      }, {});
      const result = await saveRuntimeSettings(buildSaveRows(image, marketTools, modelRows));
      setRuntimeSettings?.(Array.isArray(result?.settings) ? result.settings : getRuntimeSettings?.() || []);
      showToast('费用计算配置已保存', 'success');
      await reload?.();
    } catch (error) {
      console.error('saveCostCalculationSettings failed', error);
      showToast(error.message || '保存失败', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  return {
    getDomConfiguration,
    getPersistedConfiguration,
    getSettingMap,
    save,
  };
}

function buildSaveRows(image, marketTools, modelRows) {
  return [
    { key: COST_KEYS.image.fee, value: image.totalFee },
    { key: COST_KEYS.image.units, value: image.totalUnits },
    { key: COST_KEYS.marketTools.sellersprite.cycle, value: marketTools.sellersprite.billingCycle || 'month' },
    { key: COST_KEYS.marketTools.sellersprite.fee, value: marketTools.sellersprite.totalFee },
    { key: COST_KEYS.marketTools.sellersprite.calls, value: marketTools.sellersprite.totalCalls },
    { key: COST_KEYS.marketTools.sellersprite.balance, value: marketTools.sellersprite.openingBalance },
    { key: COST_KEYS.marketTools.sorftime.cycle, value: marketTools.sorftime.billingCycle || 'month' },
    { key: COST_KEYS.marketTools.sorftime.fee, value: marketTools.sorftime.totalFee },
    { key: COST_KEYS.marketTools.sorftime.calls, value: marketTools.sorftime.totalCalls },
    { key: COST_KEYS.marketTools.sorftime.balance, value: marketTools.sorftime.openingBalance },
    { key: COST_KEYS.modelRates, value: modelRows },
  ];
}

function cssEscape(value) {
  return String(value || '').replace(/"/g, '\\"');
}
